import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useRpc } from '../../data/people.ts'
import { say, type Plan } from '../../data/plan.ts'
import { longDay, WORK_ROLES } from '../../data/planUi.ts'
import { peopleInRotation, proposeRotation, type Proposal, type RotationInputs, type RotationResult } from '../../data/rotation.ts'
import { fonctionOf, useEmployments, useJobTitles } from '../../data/staff.ts'
import { Banner, errorText, Field } from '../../ui/components.tsx'
import { HISTORY_WEEKS, reasonText, unfilledText } from './shared.ts'

/** The rule of `data/rotation.ts`, in its order, shown word for word. */
const RULE = ['plan.rotation.rule.eligible', 'plan.rotation.rule.present', 'plan.rotation.rule.fewest', 'plan.rotation.rule.longest', 'plan.rotation.rule.lightest', 'plan.rotation.rule.name'] as const

interface Sent { proposal: Proposal; state: 'pending' | 'ok' | 'error' | 'skipped'; text: string }
/** A line the planner unticked is remembered by job AND person: a recomputed proposal for the same seat is another person, ticked again. */
const lineKey = (p: Pick<Proposal, 'job_id' | 'person_id'>) => `${p.job_id}:${p.person_id}`

/** "Proposer un roulement" (M18, ADR-108): who could do what this week so that the club's tasks turn among its people. ADVICE, computed here each time from what the
 *  server says (`plan_rotation_inputs`) and never stored. Nothing is saved until the head greenkeeper applies the lines they kept; right before sending, the lines are
 *  checked again against what the server holds now (an absence entered meanwhile, a seat taken from another device), and each line is an ordinary `assignment.create`,
 *  which the server checks once more. M23 item 28: the pool follows the FONCTION on the week (Réglages › Fonctions, `hr.job_title.in_rotation`, read from the device's
 *  mirror through `fonctionOf`): a person whose fonction is in is ticked, the others are not, and a tap adds or leaves out that person for this week only (page state;
 *  the device remembers nothing). */
export function RotationPanel({ days, plan, lang, refresh, onProposals, onApplied, onClose }: {
  /** the seven days of the week (the grid may show fewer on a phone: the advice is about the whole week) */
  days: string[]; plan: Plan; lang: string
  /** bumped by the page after its own changes (an assignment sent from the sheet, an absence saved), so the advice is read again */
  refresh: number
  onProposals(lines: Proposal[]): void
  onApplied(): void
  onClose(): void
}) {
  const { t } = useTranslation()
  const { org, site, sendAndWait, transport } = useClub()
  const [weeks, setWeeks] = useState<number>(8)
  const employments = useEmployments(); const titles = useJobTitles()
  /** who the planner ticked or unticked by hand, for this week only (true: in, false: out) */
  const [hand, setHand] = useState<ReadonlyMap<string, boolean>>(new Map())
  const args = { p_org: org.org_id, p_site: site.site_id, p_from: days[0], p_to: days[days.length - 1], p_history_days: weeks * 7 }
  const rpc = useRpc<RotationInputs>('plan_rotation_inputs', args)
  const reload = useRef(rpc.reload)
  useEffect(() => { reload.current = rpc.reload })
  const seen = useRef(refresh)
  useEffect(() => { if (refresh !== seen.current) { seen.current = refresh; void reload.current() } }, [refresh])
  const data = rpc.fresh && rpc.data ? rpc.data : null
  const week = days[0]!
  // the pool before the planner touches anything: the people whose fonction on the week (its first day) is in the rotation
  const byFonction = useMemo(() => peopleInRotation(data?.people ?? [], (id) => fonctionOf(employments ?? [], titles ?? [], id, site.site_id, week)), [data, employments, titles, site.site_id, week])
  const include = useMemo(() => [...hand].filter(([, on]) => on).map(([id]) => id), [hand])
  const exclude = useMemo(() => [...hand].filter(([, on]) => !on).map(([id]) => id), [hand])
  const result = useMemo<RotationResult | 'failed' | null>(() => {
    if (!data) return null
    try { return proposeRotation(data, { workRoles: WORK_ROLES, byFonction, include, exclude }) } catch { return 'failed' }
  }, [data, byFonction, include, exclude])
  const [dropped, setDropped] = useState<ReadonlySet<string>>(new Set())
  const [sent, setSent] = useState<Sent[] | null>(null)
  const [running, setRunning] = useState(false)
  useEffect(() => { setSent(null); setDropped(new Set()); setHand(new Map()) }, [week])          // another week: its own lines and its own ticks, nothing left over from the last one
  const proposals = useMemo(() => (result && result !== 'failed' ? result.proposals : []), [result])
  const kept = useMemo(() => proposals.filter((p) => !dropped.has(lineKey(p))), [proposals, dropped])
  useEffect(() => { onProposals(running ? [] : kept) }, [kept, running, onProposals])
  useEffect(() => () => onProposals([]), [onProposals])

  const workers = useMemo(() => (data?.people ?? []).filter((p) => p.roles.some((r) => WORK_ROLES.includes(r))), [data])
  const people = new Map((data?.people ?? []).map((p) => [p.person_id, p.display_name]))
  const tags = new Map((data?.machines ?? []).map((m) => [m.asset_id, m.readable_tag]))
  const titleOf = (jobId: string) => plan.byId.get(jobId)?.title_text ?? say(data?.slots.find((s) => s.job_id === jobId)?.title, lang)
  const lineText = (p: Proposal) => [titleOf(p.job_id), people.get(p.person_id) ?? '?', p.asset_id ? tags.get(p.asset_id) ?? '?' : ''].filter(Boolean).join(' · ')
  const toggle = (p: Proposal) => setDropped((cur) => { const next = new Set(cur); const k = lineKey(p); if (next.has(k)) next.delete(k); else next.add(k); return next })
  const isIn = (id: string) => hand.get(id) ?? byFonction.has(id)
  const toggleWorker = (id: string) => setHand((cur) => { const next = new Map(cur); const on = !isIn(id); if (on === byFonction.has(id)) next.delete(id); else next.set(id, on); return next })
  const allKept = kept.length === proposals.length
  const toggleAll = () => setDropped(allKept ? new Set(proposals.map(lineKey)) : new Set())

  const apply = async () => {
    const lines: Sent[] = kept.map((proposal) => ({ proposal, state: 'pending', text: '' }))
    setSent(lines); setRunning(true)
    try {
      // what the server holds NOW: a line whose person is away that day, already busy in that day and group, or whose job no longer has a seat is not sent
      let now: RotationInputs | null = null
      try { now = await transport.rpc<RotationInputs>('plan_rotation_inputs', args) } catch { now = null }
      const off = new Set((now?.availability ?? []).filter((a) => a.status === 'off').map((a) => `${a.person_id}:${a.local_date}`))
      const busy = new Set((now?.assignments ?? []).map((a) => `${a.person_id}:${a.local_date}:${a.sequence_group}`))
      const seats = new Map((now?.slots ?? []).map((s) => [s.job_id, (s.required_crew ?? 1) - s.crew.length]))
      for (let i = 0; i < lines.length; i++) {
        const p = lines[i]!.proposal
        let state: Sent['state'] = 'error', text = '', queued = false
        const left = seats.get(p.job_id) ?? 0
        if (!now) text = t('errors.network')
        else if (off.has(`${p.person_id}:${p.local_date}`) || busy.has(`${p.person_id}:${p.local_date}:${p.sequence_group}`) || left <= 0) { state = 'skipped'; text = t('plan.rotation.changed') }
        else {
          try {
            const v = await sendAndWait('assignment.create@1', { assignment_id: newId(), job_id: p.job_id, person_id: p.person_id, ...(p.asset_id ? { asset_id: p.asset_id } : {}) })
            if (v.status === 'applied' || v.status === 'applied_with_exception') {
              state = 'ok'; text = v.exceptions.map((c) => errorText(t, c)).join(' ')
              seats.set(p.job_id, left - 1); busy.add(`${p.person_id}:${p.local_date}:${p.sequence_group}`)
            } else if (v.status === 'queued') { queued = true; text = t('errors.network') } else text = errorText(t, v.code)
          } catch (e) { text = e instanceof Error && e.message ? e.message : t('errors.unknown') }
        }
        setSent((cur) => cur && cur.map((l, j) => (j === i ? { ...l, state, text } : l)))
        if (queued || !now) break                                    // offline: the rest waits for the planner, not for the network
      }
      setSent((cur) => cur && cur.map((l) => (l.state === 'pending' ? { ...l, state: 'error', text: t('plan.rotation.notSent') } : l)))
      onApplied(); await reload.current()                          // the advice is read again from what the server now holds
    } finally { setRunning(false); setDropped(new Set()) }
  }

  const unavailable = !rpc.online ? t('common.onlineOnly') : rpc.error === 'RANGE_INVALID' ? t('plan.rotation.failed') : rpc.error ? errorText(t, rpc.error) : null
  const total = proposals.length
  return (
    <section className="card flex flex-col gap-3" data-testid="rotation-panel" aria-labelledby="rotation-title">
      <div className="flex items-start justify-between gap-2">
        <h2 id="rotation-title" className="text-xl font-bold">{t('plan.rotation.open')}</h2>
        <button type="button" className="btn !px-4" onClick={onClose} disabled={running} data-testid="rotation-close">{t('common.close')}</button>
      </div>
      {sent && (
        <ul className="flex flex-col gap-1 text-sm" data-testid="rotation-progress" aria-live="polite">
          {sent.map((l) => (
            <li key={l.proposal.key} className="flex flex-wrap gap-x-2" data-testid="rotation-result" data-job={l.proposal.job_id} data-person={l.proposal.person_id} data-state={l.state}>
              <span className="font-medium">{lineText(l.proposal)}</span>
              <span className={l.state === 'ok' ? 'text-brand dark:text-fairway' : l.state === 'error' ? 'text-flag dark:text-red-300' : 'opacity-70'}>
                {l.state === 'pending' ? t('plan.rotation.pending') : l.state === 'ok' ? [t('plan.rotation.done'), l.text].filter(Boolean).join(' ') : l.text}
              </span>
            </li>
          ))}
        </ul>
      )}
      {unavailable ? <Banner tone="warn"><span data-testid="rotation-unavailable">{unavailable}</span></Banner> : (
        <>
          <p className="text-sm">{t('plan.rotation.intro', { count: weeks })}</p>
          <details className="rounded-xl bg-fairway-light p-3 text-sm dark:bg-night">
            <summary className="min-h-11 cursor-pointer content-center font-semibold">{t('plan.rotation.ruleTitle')}</summary>
            <ol className="mt-1 list-decimal pl-5" data-testid="rotation-rule">{RULE.map((k) => <li key={k} className="py-0.5">{t(k)}</li>)}</ol>
            <p className="mt-1">{t('plan.rotation.ruleMachine')}</p>
          </details>
          <Field label={t('plan.rotation.weeks')} htmlFor="rotation-weeks">
            <select id="rotation-weeks" className="input sm:max-w-xs" value={weeks} disabled={running} onChange={(e) => { setWeeks(Number(e.target.value)); setDropped(new Set()) }} data-testid="rotation-weeks">
              {HISTORY_WEEKS.map((n) => <option key={n} value={n}>{t('plan.rotation.weeksOption', { count: n })}</option>)}
            </select>
          </Field>
          {workers.length > 0 && (
            <fieldset className="flex flex-col gap-1.5" data-testid="rotation-people">
              <legend className="text-sm font-semibold">{t('plan.rotation.who')}</legend>
              <p className="meta">{t('plan.rotation.whoHelp')}</p>
              <div className="flex flex-wrap gap-1.5">
                {workers.map((p) => {
                  const on = isIn(p.person_id)
                  return (
                    <button key={p.person_id} type="button" aria-pressed={on} disabled={running} onClick={() => { toggleWorker(p.person_id); setDropped(new Set()) }} data-testid="rotation-person" data-person={p.person_id} data-fonction={byFonction.has(p.person_id) ? 'in' : 'out'}
                      className={`min-h-11 rounded-full border-2 px-3 text-sm font-semibold ${on ? 'border-brand bg-fairway-light text-brand-dark dark:border-emerald-600 dark:bg-night dark:text-fairway' : 'border-slate-300 bg-white text-slate-500 line-through dark:border-night-line dark:bg-night-card dark:text-slate-400'}`}>
                      {p.display_name}
                    </button>
                  )
                })}
              </div>
              <Link to="/settings/job-titles" className="btn self-start" data-testid="rotation-fonctions">{t('plan.rotation.fonctionsLink')}</Link>
            </fieldset>
          )}
          {result === null && <p className="opacity-70">{t('common.loading')}</p>}
          {result === 'failed' && <Banner tone="error">{t('errors.unknown')}</Banner>}
          {result && result !== 'failed' && data && (
            <>
              {total === 0 && result.unfilled.length === 0 && (
                <p className="opacity-80" data-testid="rotation-empty">{data.today > data.to ? t('plan.rotation.emptyPast') : data.today > data.from ? t('plan.rotation.emptyFromToday') : t('plan.rotation.empty')}</p>
              )}
              {total > 1 && <button type="button" className="btn !min-h-11 self-start !px-4" disabled={running} onClick={toggleAll} data-testid="rotation-toggle-all">{allKept ? t('plan.rotation.uncheckAll') : t('plan.rotation.checkAll')}</button>}
              {days.map((d) => {
                const lines = proposals.filter((p) => p.local_date === d)
                if (lines.length === 0) return null
                return (
                  <details key={d} open={total <= 24 || lines.length <= 6} className="flex flex-col gap-1.5" data-testid="rotation-day" data-day={d}>
                    <summary className="min-h-11 cursor-pointer content-center font-bold first-letter:uppercase">{t('plan.rotation.dayCount', { day: longDay(d, lang), count: lines.length })}</summary>
                    <div className="mt-1 flex flex-col gap-1.5">
                      {lines.map((p) => (
                        <label key={p.key} className="flex min-h-14 items-start gap-3 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid="rotation-line" data-job={p.job_id} data-person={p.person_id} data-asset={p.asset_id ?? ''}>
                          <input type="checkbox" className="mt-0.5 size-6 shrink-0 accent-brand" checked={!dropped.has(lineKey(p))} disabled={running} onChange={() => toggle(p)} data-testid="rotation-check" />
                          <span className="flex min-w-0 flex-col">
                            <span className="font-semibold">{lineText(p)}</span>
                            <span className="meta" data-testid="rotation-why">{reasonText(t, p, data.history, weeks, data.today)}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </details>
                )
              })}
              {result.unfilled.length > 0 && (
                <details className="flex flex-col gap-1" data-testid="rotation-unfilled-fold">
                  <summary className="min-h-14 cursor-pointer content-center font-bold">{t('plan.rotation.unfilledSummary', { count: result.unfilled.length })}</summary>
                  <ul className="mt-1 flex flex-col gap-1 text-sm">
                    {result.unfilled.map((u) => <li key={u.job_id} className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900 first-letter:uppercase dark:bg-amber-950/50 dark:text-amber-100" data-testid="rotation-unfilled" data-job={u.job_id}>{unfilledText(t, u, titleOf(u.job_id), lang)}</li>)}
                  </ul>
                </details>
              )}
              {total > 0 && (
                <div className="sticky bottom-[calc(5rem_+_var(--gk-safe-bottom))] z-[5] -mx-4 flex items-center gap-2 border-t border-slate-200 bg-white/95 px-4 py-2 backdrop-blur dark:border-night-line dark:bg-night-card/95">
                  <button type="button" className="btn btn-primary" disabled={running || kept.length === 0} onClick={() => void apply()} data-testid="rotation-apply">
                    {running ? t('plan.rotation.applying') : t('plan.rotation.apply', { count: kept.length })}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}
